/**
 * The fixed vocabulary of the notice board: kinds, placements, and how each one
 * looks and reads.
 *
 * Kinds are an enum on the wire precisely so they can be translated — an admin
 * typing their own section name would put an English heading in front of every
 * non-English user, which is the one thing a fifteen-language portal cannot
 * afford. Everything that turns a kind into pixels or words is therefore
 * derived here rather than stored.
 *
 * `MESSAGE_KIND_ORDER` is the reading order of the board, and it is not
 * alphabetical: an alert is something you must know now, a hot topic is what
 * everyone is already asking about, news can wait, and help is evergreen. The
 * API sorts *within* a kind (by rank, then newest first); this array decides
 * which block comes first.
 */

import { Flame, type IconComponent, LifeBuoy, Newspaper, TriangleAlert } from "@norican/icons";
import type { BadgeTone, CardProps } from "@norican/react";

import { useT } from "@/i18n";
import type { Message, MessageKind, MessagePlacement } from "@/lib/api";

export const MESSAGE_KIND_ORDER = [
  "ALERT",
  "HOT_TOPIC",
  "NEWS",
  "HELP",
] as const satisfies readonly MessageKind[];

/** Every placement an admin may choose, in decreasing visibility. */
export const MESSAGE_PLACEMENTS = [
  "HOME",
  "CENTER",
  "HIDDEN",
] as const satisfies readonly MessagePlacement[];

export const MESSAGE_KIND_ICON: Record<MessageKind, IconComponent> = {
  ALERT: TriangleAlert,
  HOT_TOPIC: Flame,
  NEWS: Newspaper,
  HELP: LifeBuoy,
};

/**
 * Tone per kind, shared by the card accent and the kind badge so a notice reads
 * the same whichever surface it appears on.
 *
 * Only `ALERT` is danger. Colouring more than one kind red is how a board stops
 * being read: if everything is urgent, the genuinely urgent row loses.
 *
 * DS-GAP (v2.0.1): `CardAccentTone` is declared in the design system but not
 * re-exported from its package index, so the tone union has to be recovered
 * from `CardProps` rather than named. Swap this for the exported alias when it
 * appears.
 */
export type MessageTone = BadgeTone & NonNullable<CardProps["accentTone"]>;

export const MESSAGE_KIND_TONE: Record<MessageKind, MessageTone> = {
  ALERT: "danger",
  HOT_TOPIC: "warning",
  NEWS: "accent",
  HELP: "neutral",
};

/** Translated label for a kind, e.g. `ALERT` → "Alert". */
export function useMessageKindLabel() {
  const { t } = useT("messages");
  return (kind: MessageKind): string => t(`kind.${kind}` as "kind.ALERT");
}

/** Translated label for a placement, e.g. `HOME` → "Home page". */
export function useMessagePlacementLabel() {
  const { t } = useT("messages");
  return (placement: MessagePlacement): string => t(`placement.${placement}` as "placement.HOME");
}

export interface MessageGroup {
  kind: MessageKind;
  items: Message[];
}

/**
 * Splits a flat list into kind blocks in `MESSAGE_KIND_ORDER`, dropping empty
 * ones. Order *within* a group is left exactly as the API returned it — rank
 * then newest first — because rank is the admin's explicit arrangement and
 * re-sorting here would silently overrule it.
 */
export function groupMessagesByKind(messages: readonly Message[]): MessageGroup[] {
  return MESSAGE_KIND_ORDER.map((kind) => ({
    kind,
    items: messages.filter((message) => message.kind === kind),
  })).filter((group) => group.items.length > 0);
}

/**
 * Sources whose words are not the admin's to rewrite.
 *
 * `IVANTI` is the tenant's text; `BUILTIN` ships with the app and lives in
 * fifteen translation catalogues. Both are placeable and rankable and neither
 * is editable, and the server enforces that with a 409 — so this predicate
 * exists to keep the UI from offering an edit the API will refuse. It is
 * shared rather than re-derived per component precisely because the admin
 * screen was first written against `IVANTI` alone, and `BUILTIN` arriving made
 * every one of those checks quietly wrong.
 */
export type ReadOnlySource = "IVANTI" | "BUILTIN";

export function isReadOnlySource(source: Message["source"]): source is ReadOnlySource {
  return source === "IVANTI" || source === "BUILTIN";
}
